import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { FollowableName } from "@/components/FollowableName";
import { Trophy, Search, Loader2, Crown, MapPin, CalendarDays, Ticket, ExternalLink } from "lucide-react";

const EVENT_LABEL = {
  "Ladies Barrel Racing": "Barrel Racing",
  "Ladies Breakaway Roping": "Breakaway",
  "TR Header": "Team Roping — Header",
  "TR Heeler": "Team Roping — Heeler",
};
const label = (ev) => EVENT_LABEL[ev] || ev;

function QualRow({ rank, name, hometown, earnings, entered, cut, mine, m, title }) {
  return (
    <div data-testid={`cfr-row-${rank}`}
      className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 ${m ? "border-white/5" : "border-[#F1F3F5]"} ${mine ? (m ? "bg-[#C8000F]/15" : "bg-[#C8000F]/8") : ""} ${cut ? "border-b-2 border-b-[#B8860B]/70" : ""}`}>
      <span className="w-7 text-center font-display font-bold text-sm text-[#3B6B1E]">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold truncate flex items-center gap-1.5 ${title}`}>
          <FollowableName name={name} />
          {mine ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#C8000F] text-white">YOU</span> : null}
          {rank === 1 ? <Crown className="w-3.5 h-3.5 text-[#B8860B]" /> : null}
        </div>
        {hometown ? <div className="text-[11px] text-slate-400 truncate">{hometown}</div> : null}
      </div>
      {entered != null ? <span className="text-[11px] text-slate-400 hidden sm:block w-16 text-right">{entered} entered</span> : null}
      <span className="font-display font-bold text-sm text-[#3B6B1E] w-24 text-right">{money(earnings)}</span>
    </div>
  );
}

export default function Cfr() {
  const m = useIsMobile();
  const [data, setData] = useState(null);
  const [event, setEvent] = useState(null);
  const [q, setQ] = useState("");
  const [me, setMe] = useState("");
  const [results, setResults] = useState(null);
  const [round, setRound] = useState(null);
  const [totals, setTotals] = useState(null);
  const [tEvent, setTEvent] = useState(null);
  const [tab, setTab] = useState("totals");

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";

  useEffect(() => {
    api.get("/cfr").then((r) => { setData(r.data); if (r.data.events?.length) setEvent(r.data.events[0].event); }).catch(() => setData({ events: [] }));
    api.get("/cfr/results").then((r) => {
      setResults(r.data);
      const rs = r.data?.rounds || [];
      if (rs.length) setRound(rs[rs.length - 1].round); else setTab("qualifiers");
    }).catch(() => { setResults({ rounds: [] }); setTab("qualifiers"); });
    api.get("/cfr/totals").then((r) => {
      setTotals(r.data);
      const es = r.data?.events || [];
      if (es.length) setTEvent(es[0].event); else setTab("results");
    }).catch(() => { setTotals({ events: [] }); setTab("results"); });
    api.get("/me/competitor").then((r) => setMe((r.data?.name || "").trim().toLowerCase())).catch(() => {});
  }, []);

  const current = useMemo(() => (data?.events || []).find((e) => e.event === event), [data, event]);
  const rows = useMemo(() => {
    const list = current?.qualifiers || [];
    if (!q.trim()) return list;
    const n = q.trim().toLowerCase();
    return list.filter((r) => (r.name || "").toLowerCase().includes(n) || (r.hometown || "").toLowerCase().includes(n));
  }, [current, q]);

  const info = data?.info;
  const curRound = useMemo(() => (results?.rounds || []).find((r) => r.round === round), [results, round]);
  const hasResults = (results?.rounds || []).length > 0;
  const curT = useMemo(() => (totals?.events || []).find((e) => e.event === tEvent), [totals, tEvent]);
  const hasTotals = (totals?.events || []).length > 0;

  return (
    <div className={page} data-testid="cfr-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-[#0C1017] text-white px-4 sm:px-8 py-8 sm:py-10 border-b border-black/20">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 15% 20%, #C8000F 0%, transparent 45%), radial-gradient(circle at 85% 80%, #B8860B 0%, transparent 45%)" }} />
        <div className="relative max-w-3xl">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-[#E8B23A]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E8B23A]">CPRA Championship</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-none">Canadian Finals Rodeo</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#E8B23A]" /> {info?.dates || "October 2026"}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#E8B23A]" /> {info ? `${info.venue} · ${info.city}` : "Edmonton, AB"}</span>
            {info?.rounds ? <span className="text-slate-400">{info.rounds} go-rounds + aggregate</span> : null}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-2.5" data-testid="cfr-total-payout">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total payout</div>
              <div className="font-display text-2xl font-extrabold text-[#E8B23A]">{info ? money(info.total_payout) : "—"}</div>
            </div>
            {info?.tickets_url && (
              <a href={info.tickets_url} target="_blank" rel="noopener noreferrer" data-testid="cfr-tickets-link"
                className="flex items-center gap-2 text-sm font-semibold bg-[#C8000F] hover:bg-[#9E0008] text-white px-4 py-2.5 rounded-lg transition-colors">
                <Ticket className="w-4 h-4" /> Tickets & info
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Tabs: Results / Qualifiers */}
      <div className={`px-4 sm:px-8 pt-4 ${m ? "" : ""}`}>
        <div className="flex gap-2 max-w-3xl overflow-x-auto" data-testid="cfr-tabs">
          {[["totals", "Season + CFR $"], ["results", "Go-Round Results"], ["qualifiers", "Qualifiers"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cfr-tab-${k}`}
              className={`text-sm font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${tab === k
                ? "bg-[#C8000F] text-white"
                : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
              {lbl}{k === "totals" && totals?.live_round ? ` · R${totals.live_round}` : ""}
            </button>
          ))}
        </div>
      </div>

      {tab === "totals" && (
        <div className="px-4 sm:px-8 py-4 max-w-3xl" data-testid="cfr-totals">
          {totals === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          ) : !hasTotals ? (
            <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
              <Trophy className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
              <div className={`font-display font-bold ${title}`}>Live totals appear once the CFR is underway</div>
            </div>
          ) : (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 mb-2" data-testid="cfr-total-events">
                {totals.events.map((e) => (
                  <button key={e.event} onClick={() => setTEvent(e.event)} data-testid={`cfr-total-event-${e.event.replace(/\s/g, "-")}`}
                    className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg transition-colors ${e.event === tEvent
                      ? "bg-[#C8000F] text-white"
                      : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
                    {e.display || e.event}
                  </button>
                ))}
              </div>
              <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="cfr-total-table">
                <div className={`flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${m ? "bg-white/5 text-slate-400 border-b border-white/5" : "bg-[#F8F9FB] text-slate-400 border-b border-[#F1F3F5]"}`}>
                  <span className="w-6">#</span>
                  <span className="flex-1">Contestant</span>
                  <span className="w-20 text-right">Season</span>
                  <span className="w-16 text-right">CFR</span>
                  <span className="w-20 text-right">Total</span>
                </div>
                {(curT?.rows || []).map((r, i) => {
                  const mine = me && (r.name || "").trim().toLowerCase() === me;
                  return (
                    <div key={`${r.back}-${r.name}`} data-testid={`cfr-total-row-${i + 1}`}
                      className={`flex items-center gap-2 px-3 py-2.5 border-b last:border-b-0 ${m ? "border-white/5" : "border-[#F1F3F5]"} ${mine ? (m ? "bg-[#C8000F]/15" : "bg-[#C8000F]/8") : ""}`}>
                      <span className="w-6 font-display font-bold text-sm text-[#3B6B1E]">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${title}`}><FollowableName name={r.name} />{mine ? <span className="ml-1.5 text-[10px] font-bold px-1 py-0.5 rounded bg-[#C8000F] text-white">YOU</span> : null}</div>
                        {r.hometown ? <div className="text-[11px] text-slate-400 truncate">{r.hometown}</div> : null}
                      </div>
                      <span className="w-20 text-right text-xs text-slate-400">{money(r.season)}</span>
                      <span className="w-16 text-right text-xs font-semibold text-[#B8860B]">{r.cfr ? money(r.cfr) : "—"}</span>
                      <span className="w-20 text-right text-sm font-display font-bold text-[#3B6B1E]">{money(r.total)}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Season + CFR winnings · live from cfr.ca{totals?.live_round ? ` · through Round ${totals.live_round}` : ""}.</p>
            </>
          )}
        </div>
      )}

      {tab === "results" && (
        <div className="px-4 sm:px-8 py-4 max-w-3xl" data-testid="cfr-results">
          {results === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          ) : !hasResults ? (
            <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
              <Trophy className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
              <div className={`font-display font-bold ${title}`}>Go-round results post after each performance</div>
              <p className="text-sm text-slate-400 mt-1">Winners for each round appear here live, straight from the official cfr.ca results sheet.</p>
            </div>
          ) : (
            <>
              {/* Round tabs */}
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 mb-1" data-testid="cfr-rounds">
                {results.rounds.map((r) => (
                  <button key={r.round} onClick={() => setRound(r.round)} data-testid={`cfr-round-${r.round}`}
                    className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg transition-colors ${r.round === round
                      ? "bg-[#3B6B1E] text-white"
                      : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
                    Round {r.round}
                  </button>
                ))}
              </div>

              {curRound?.headline && (
                <div className={`flex items-center gap-3 rounded-xl border overflow-hidden mb-3 ${card}`}>
                  {curRound.photo ? <img src={curRound.photo} alt="" className="w-20 h-16 object-cover shrink-0" /> : null}
                  <a href={curRound.source_url} target="_blank" rel="noopener noreferrer"
                    className={`text-sm font-semibold py-2 pr-3 hover:text-[#C8000F] ${title}`} data-testid="cfr-round-headline">
                    {curRound.headline}
                  </a>
                </div>
              )}

              <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="cfr-round-table">
                <div className={`flex items-center gap-3 px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${m ? "bg-white/5 text-slate-400 border-b border-white/5" : "bg-[#F8F9FB] text-slate-400 border-b border-[#F1F3F5]"}`}>
                  <span className="flex-1">Event · Winner</span>
                  <span className="w-20 text-right">Score/Time</span>
                  <span className="w-20 text-right">Payout</span>
                </div>
                {(curRound?.winners || []).map((w) => (
                  <div key={w.event} data-testid={`cfr-win-${w.event.replace(/\s/g, "-")}`}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 ${m ? "border-white/5" : "border-[#F1F3F5]"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-[#B8860B]">{label(w.event)}</div>
                      <div className={`text-sm font-semibold truncate ${title}`}><FollowableName name={w.name} /></div>
                    </div>
                    <span className="w-20 text-right text-sm font-display font-bold text-[#3B6B1E]">{w.score_or_time || "—"}</span>
                    <span className="w-20 text-right text-sm font-semibold text-slate-400">{w.payout != null ? money(w.payout) : "—"}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Go-round winners · official results live from cfr.ca/the-rodeo/live-results.</p>
            </>
          )}
        </div>
      )}

      {tab === "qualifiers" && (data === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (data.events || []).length === 0 ? (
        <div className="px-4 sm:px-8 py-10 max-w-3xl">
          <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
            <Trophy className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
            <div className={`font-display font-bold ${title}`}>Qualifiers not loaded yet</div>
            <p className="text-sm text-slate-400 mt-1">The CFR qualifiers appear here once the CPRA season standings are imported.</p>
          </div>
        </div>
      ) : (
        <div className="px-4 sm:px-8 py-5 max-w-3xl">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className={`font-display font-bold text-lg ${title}`}>Qualifiers</h2>
            <span className="text-xs text-slate-400">Top 12 per event{data?.season?.year ? ` · ${data.season.year} CPRA season` : ""}</span>
          </div>

          {/* Event tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="cfr-events">
            {(data.events || []).map((e) => (
              <button key={e.event} onClick={() => { setEvent(e.event); setQ(""); }} data-testid={`cfr-event-${e.event.replace(/\s/g, "-")}`}
                className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg transition-colors ${e.event === event
                  ? "bg-[#C8000F] text-white"
                  : m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
                {label(e.event)}
              </button>
            ))}
          </div>

          {/* Event payout + cut line */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 mb-3 text-[11px]">
            {current?.payout != null && (
              <span className="text-slate-400">Event payout: <span className="font-bold text-[#3B6B1E]">{money(current.payout)}</span></span>
            )}
            {current?.cfr_cutoff != null && (
              <span className="text-slate-400">CFR cut line (12th): <span className="font-bold text-[#B8860B]">{money(current.cfr_cutoff)}</span></span>
            )}
          </div>

          {/* Search */}
          <div className={`flex items-center gap-2 mb-3 rounded-lg border px-3 ${card}`}>
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search athlete or hometown" data-testid="cfr-search"
              className={`flex-1 bg-transparent py-2.5 text-sm focus:outline-none ${m ? "text-slate-100 placeholder:text-slate-500" : "text-[#14181F] placeholder:text-slate-400"}`} />
          </div>

          <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="cfr-qualifiers">
            {rows.map((r) => (
              <QualRow key={`${r.rank}-${r.name}`} rank={r.rank} name={r.name} hometown={r.hometown} earnings={r.earnings}
                entered={r.entered} cut={r.rank === 12 && !q.trim()} mine={me && (r.name || "").trim().toLowerCase() === me}
                m={m} title={title} />
            ))}
            {rows.length === 0 && <div className="px-3 py-8 text-center text-sm text-slate-400">No athletes match “{q}”.</div>}
          </div>

          <a href={info?.source_url || "https://rodeocanada.com/canadian-finals-rodeo/"} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#C8000F] mt-3" data-testid="cfr-source-link">
            Source: rodeocanada.com <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      ))}
    </div>
  );
}
